import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";
import Typeahead from "@components/shared/Typeahead/Typeahead";
import { TypeaheadFn } from "@components/shared/TypeaheadFn/TypeaheadFn";
import { BulkGenerateSerialsSidebar } from "@components/shared/BulkGenerateSerialsSidebar/BulkGenerateSerialsSidebar";
import { BulkCreateSerialsOutcome } from "@components/shared/BulkGenerateSerialsSidebar/BulkCreateSerialsOutcome";
import { useAppContextNonNull } from "@contexts/AppContext";
import { buildRestUrlWithQuery, buildAuthHeadersForFetch } from "@services/RestService";
import SerialService from "@services/SerialService";
import RestEndpointsEnum from "@models/RestEndpointsEnum";
import EndpointsEnum from "@models/EndpointsEnum";
import ICatalog from "@models/ICatalog";
import ICatalog_Item from "@models/ICatalog_Item";
import IOnGetCatalogsDTO from "@models/DTO/IOnGetCatalogsDTO";
import IOnTypeaheadCatalogItemDTO from "@models/DTO/IOnTypeaheadCatalogItemDTO";
import ITypeaheadCatalogItemDTO from "@models/DTO/ITypeaheadCatalogItemDTO";
import IProcessSerialDTO from "@models/DTO/IProcessSerialDTO";
import { IBulkCreateSerialError } from "@models/DTO/IOnBulkCreateSerialsDTO";
import { useVendorSourceTypeahead } from "@hooks/useVendorSourceTypeahead";

type Props = {
    cancel: (refresh?: boolean) => void;
};

type FormData = {
    catalogGuid: string;
    catalogItemGuid: string;
    serial: string;
};

const validationSchema = yup.object().shape({
    catalogGuid: yup.string().required("Please select a catalog"),
    catalogItemGuid: yup.string().required("Please select a SKU"),
    serial: yup.string().trim().required("Please enter a serial number"),
});

export const CreateSerialSidebar = (props: Props) => {
    const { socket, userModel, supplierModel, vendorModel } = useAppContextNonNull();

    const [mode, setMode] = useState<"single" | "bulk">("single");
    const [catalogs, setCatalogs] = useState<ICatalog[]>([]);
    const [typeaheadItems, setTypeaheadItems] = useState<ICatalog_Item[]>([]);
    const [selectedCatalogItem, setSelectedCatalogItem] = useState<ICatalog_Item | undefined>(undefined);
    const [clearTypeahead, setClearTypeahead] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    // OR-1550: set synchronously on submit so a second Enter/click is dropped before the first request returns
    // (isSaving only takes effect on the next render, after yup validation has already been awaited).
    const isSubmittingRef = useRef(false);
    // Something was created: refresh the Serials list when the sidebar closes.
    const [hasCreated, setHasCreated] = useState(false);
    const [bulkOutcome, setBulkOutcome] = useState<{ created: number; skipped: IBulkCreateSerialError[] } | null>(null);

    const close = () => props.cancel(hasCreated);

    const {
        vendorSourceOptions,
        selectedVendorSource,
        resetVendorSource,
        lookupVendorSource,
        selectVendorSource,
        clearVendorSource,
        resetVendorSourceFn,
    } = useVendorSourceTypeahead(userModel, supplierModel);

    const {
        setValue,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<FormData>({
        resolver: yupResolver(validationSchema) as any,
        defaultValues: {
            catalogGuid: "",
            catalogItemGuid: "",
            serial: "",
        },
    });

    const catalogGuid = watch("catalogGuid");

    // Fetch catalogs on mount
    const fetchCatalogs = useCallback(async () => {
        try {
            const url = buildRestUrlWithQuery({
                endpoint: RestEndpointsEnum.CatalogsGetCatalogs,
            });

            const headers = buildAuthHeadersForFetch({
                userModel,
                supplierModel,
                vendorModel,
            });

            const response = await fetch(url, {
                method: "GET",
                headers,
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch catalogs: ${response.statusText}`);
            }

            const data = (await response.json()) as IOnGetCatalogsDTO;
            setCatalogs(data.catalogs || []);
        } catch (error) {
            console.error("Error fetching catalogs:", error);
        }
    }, [userModel, supplierModel, vendorModel]);

    useEffect(() => {
        fetchCatalogs();
    }, []);

    // Socket listener for typeahead results
    useEffect(() => {
        const onTypeaheadCatalogItem = (data: IOnTypeaheadCatalogItemDTO) => {
            if (data) {
                setTypeaheadItems(data.catalog_items || []);
            }
        };

        socket.on(EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, onTypeaheadCatalogItem);

        return () => {
            socket.removeListener(EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, onTypeaheadCatalogItem);
        };
    }, [socket]);

    // Typeahead search function
    const doTypeahead = (searchPhrase: string) => {
        if (!searchPhrase.length || !catalogGuid) return;

        const data: ITypeaheadCatalogItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            catalogGuid,
            name: searchPhrase,
            onlyActive: true,
            limit: 100,
            options: {
                searchName: true,
                searchSku: true,
                searchDescription: false,
                searchUpc: false,
            },
        };

        socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, data);
    };

    // Handle catalog selection change
    const handleCatalogChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const guid = e.target.value;
        setValue("catalogGuid", guid);

        // Reset SKU selection
        setValue("catalogItemGuid", "");
        setSelectedCatalogItem(undefined);
        setTypeaheadItems([]);
        setClearTypeahead(true);
    };

    // Handle typeahead item selection
    const selectItem = (item: ICatalog_Item) => {
        setSelectedCatalogItem(item);
        setValue("catalogItemGuid", item.guid);
    };

    // Clear typeahead callback
    const clearTypeaheadFn = () => {
        setClearTypeahead(false);
    };

    // Handle serial input change
    const handleSerialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue("serial", e.target.value);
    };

    // Single mode: guard first, then validate and save. Enter in the serial input lands here too.
    const submitSingle = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isSubmittingRef.current) return;

        isSubmittingRef.current = true;
        setIsSaving(true);

        handleSubmit(onSubmit)(e).finally(() => {
            isSubmittingRef.current = false;
            setIsSaving(false);
        });
    };

    // Submit handler for single mode (called by submitSingle once the form is valid)
    const onSubmit = async (data: FormData) => {
        try {
            setErrorMessage("");

            const url = buildRestUrlWithQuery({
                endpoint: RestEndpointsEnum.SerialsProcessSerial,
            });

            const headers = buildAuthHeadersForFetch({
                userModel,
                supplierModel,
                vendorModel,
            });

            const body: Partial<IProcessSerialDTO> = {
                catalogItemGuid: data.catalogItemGuid,
                serial: data.serial.trim(),
            };

            const response = await fetch(url, {
                method: "POST",
                headers,
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                throw new Error(errorData?.message || `Failed to save serial: ${response.statusText}`);
            }

            props.cancel(true);
        } catch (error: any) {
            console.error("Error processing serial:", error);
            setErrorMessage(error.message || "Error saving serial");
        }
    };

    // Handler for bulk mode. BulkGenerateSerialsSidebar keeps its Add button disabled until this settles.
    const handleBulkAdd = async (serials: string[], reportProgress: (processed: number, total: number) => void) => {
        const catalogItemGuid = watch("catalogItemGuid");
        if (!catalogItemGuid) {
            setErrorMessage("Please select a SKU first");
            return;
        }

        try {
            setIsSaving(true);
            setErrorMessage("");
            setBulkOutcome(null);

            const result = await SerialService.bulkCreateSerials(
                {
                    catalogItemGuid,
                    serials,
                    vendorSourceId: selectedVendorSource?.id,
                },
                userModel,
                supplierModel,
                vendorModel,
                reportProgress
            );

            if (result.serials.length > 0) {
                setHasCreated(true);
            }

            if (result.errorItems.length === 0) {
                props.cancel(true);
                return;
            }

            // Some values were skipped (e.g. "Serial already exists"): stay open and say which.
            setBulkOutcome({ created: result.serials.length, skipped: result.errorItems });
        } catch (error: any) {
            console.error("Error bulk creating serials:", error);
            // Earlier requests of the range may have created serials: refresh the list on close.
            setHasCreated(true);
            setErrorMessage(error.message || "Error creating serials");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className={"CreateSerialSidebar p-2"}>
            <div className={"text-end"}>
                <OwButton centerIconClass={"fas fa-times"} fn={close} className={"btn-danger btn-sm"} disabled={isSaving} />
            </div>

            <OwTitle title={"create_serial"} />
            <hr />

            {/* Catalog Selection */}
            <div className={"mb-3"}>
                <OwLabel title={"catalog"} />
                <select className={"form-select form-select-sm"} value={catalogGuid} onChange={handleCatalogChange}>
                    <option value={""}>-- Select Catalog --</option>
                    {catalogs.map((catalog) => (
                        <option key={catalog.guid} value={catalog.guid}>
                            {catalog.name}
                        </option>
                    ))}
                </select>
                {errors.catalogGuid && <div className={"text-danger small mt-1"}>{errors.catalogGuid.message}</div>}
            </div>

            {/* SKU Typeahead */}
            <div className={"mb-3"}>
                <OwLabel title={"sku"} />
                {catalogGuid ? (
                    <Typeahead
                        typeaheadItems={typeaheadItems}
                        clearTypeaheadCallback={clearTypeaheadFn}
                        clearTypeahead={clearTypeahead}
                        titleProp="sku"
                        subTitleProp="name"
                        isSelectableFn={() => true}
                        maxResults={100}
                        placeholder="Search by SKU or Name"
                        selectedItem={selectedCatalogItem}
                        selectedItemFn={selectItem}
                        typeaheadApiFn={doTypeahead}
                        scrollableList={true}
                        defaultClassName={"form-control form-control-sm"}
                    />
                ) : (
                    <input className={"form-control form-control-sm"} disabled placeholder={"Select a catalog first"} />
                )}
                {errors.catalogItemGuid && <div className={"text-danger small mt-1"}>{errors.catalogItemGuid.message}</div>}
            </div>

            {/* Vendor Source Typeahead */}
            {supplierModel?.has_purchase_orders && (
                <div className={"mb-3"}>
                    <OwLabel title={"vendor_source"} />
                    <TypeaheadFn
                        lookupFn={lookupVendorSource}
                        availableOptions={vendorSourceOptions}
                        selectedOptionFn={selectVendorSource}
                        selectedOption={selectedVendorSource}
                        valueProp={"guid"}
                        titleProp={"name"}
                        resetTypeahead={resetVendorSource}
                        resetTypeaheadCallbackFn={resetVendorSourceFn}
                        clearSelectionFn={clearVendorSource}
                        emitSelectionSetup={{ emitObject: true }}
                        placeholder={"Search vendor source..."}
                    />
                </div>
            )}

            {/* Mode Toggle */}
            <div className={"mb-3"}>
                <div className="btn-group btn-group-sm w-100">
                    <button
                        type="button"
                        className={`btn ${mode === "single" ? "btn-primary" : "btn-outline-primary"}`}
                        disabled={isSaving}
                        onClick={() => setMode("single")}>
                        <OwLabel title={"serial_mode_single"} noBottomMargin />
                    </button>
                    <button
                        type="button"
                        className={`btn ${mode === "bulk" ? "btn-primary" : "btn-outline-primary"}`}
                        disabled={isSaving}
                        onClick={() => setMode("bulk")}>
                        <OwLabel title={"serial_mode_bulk"} noBottomMargin />
                    </button>
                </div>
            </div>

            <hr />

            {/* Single Mode */}
            {mode === "single" && (
                <form onSubmit={submitSingle}>
                    <div className={"mb-3"}>
                        <OwLabel title={"serial"} />
                        <OwFnInput fn={handleSerialChange} name={"serial"} value={watch("serial")} placeholder={"Enter serial number"} />
                        {errors.serial && <div className={"text-danger small mt-1"}>{errors.serial.message}</div>}
                    </div>

                    {errorMessage && <div className={"alert alert-danger py-2 small"}>{errorMessage}</div>}

                    <div className={"d-flex justify-content-end mt-3"}>
                        <OwButton
                            title={"save"}
                            type={"submit"}
                            className={"btn-primary btn-sm me-2"}
                            disabled={isSaving}
                            active={isSaving}
                            disableOnActive={true}
                        />
                        <OwButton title={"cancel"} fn={close} className={"btn-danger btn-sm"} disabled={isSaving} />
                    </div>
                </form>
            )}

            {/* Bulk Mode */}
            {mode === "bulk" && (
                <>
                    {!watch("catalogItemGuid") && (
                        <div className={"alert alert-warning py-2 small"}>Please select a catalog and SKU above first.</div>
                    )}
                    {errorMessage && <div className={"alert alert-danger py-2 small"}>{errorMessage}</div>}
                    {bulkOutcome && <BulkCreateSerialsOutcome created={bulkOutcome.created} skipped={bulkOutcome.skipped} />}
                    <BulkGenerateSerialsSidebar embedded onAdd={handleBulkAdd} cancel={close} />
                </>
            )}
        </div>
    );
};
